/**
 * O plano de aplicação do assistente de configuração: o que a sugestão
 * da IA vira em cada aba, o que JÁ existe no cadastro, e como gravar o
 * que a pessoa marcou — pelas rotas de catálogo que já existem.
 *
 * Função pura dos dois lados: `planPanels` transforma (sugestão,
 * catálogo) em itens com "já cadastrado" marcado, e `applyPanel` recebe
 * os itens EDITADOS e marcados e fala com a API por uma interface mínima
 * (`AssistantApi`), que é o que deixa os dois testáveis sem montar a
 * tela. A tela só mostra e deixa editar; nada é gravado sem passar por
 * aqui, e nada é gravado sem a pessoa clicar em "Aplicar".
 *
 * "Já cadastrado" é por NOME (sem acento e sem caixa, `foldText`), a
 * mesma identidade do servidor para nome de catálogo — um item assim
 * nasce desmarcado e desabilitado: aplicar de novo o que existe daria
 * 409 na rota, e desativar/editar o existente é trabalho da aba.
 */

import { errorMessage } from "@/shared/api/http";
import type {
  CitationTagGroup,
  ClassificationTerm,
  ConfigSuggestion,
  FocusCategory,
  FocusTerm,
  KeyMessage,
  PostSource,
  PromptRule,
  PublisherTier,
  Spokesperson,
  TermCategory,
  Theme,
  ThemeGroup
} from "@/shared/api/api-client";
import { foldText } from "@/shared/utils/text-search";

export type PanelId =
  | "brand"
  | "citation"
  | "focus"
  | "rules"
  | "terms"
  | "themes"
  | "messages"
  | "people"
  | "tiers";

export const PANEL_ORDER: PanelId[] = [
  "brand",
  "citation",
  "focus",
  "rules",
  "terms",
  "themes",
  "messages",
  "people",
  "tiers"
];

export const PANEL_LABELS: Record<PanelId, string> = {
  brand: "Marca e termos",
  citation: "Citação no título",
  focus: "Foco cliente / concorrente",
  rules: "Regras e premissas",
  terms: "Termos proprietários",
  themes: "Temas e subtemas",
  messages: "Mensagens-chave",
  people: "Porta-vozes",
  tiers: "Porte do publicador"
};

/** A tarefa em que as premissas de polaridade moram — a mesma que a
 *  aba "Configuração de prompt" grava. */
export const RULES_TASK = "polarity_intensity";

const SOURCE_LABEL: Record<PostSource, string> = {
  network: "Redes",
  press: "Imprensa"
};

const RULE_TYPE_LABEL: Record<string, string> = {
  global: "Regras globais",
  focus_exclusion_rule: "Exclusão",
  positivo: "Positivo",
  negativo: "Negativo",
  neutro: "Neutro",
  favoravel: "Favorável",
  desfavoravel: "Desfavorável"
};

/** O que a tela carrega do cadastro pra marcar o que já existe. */
export type Catalog = {
  brandId: number;
  brandName: string;
  brandTerms: string | null;
  themeGroups: ThemeGroup[];
  themes: Theme[];
  keyMessages: KeyMessage[];
  spokespersons: Spokesperson[];
  publisherTiers: PublisherTier[];
  focusTerms: FocusTerm[];
  classificationTerms: ClassificationTerm[];
  promptRules: PromptRule[];
  citationTags: CitationTagGroup[];
};

/** Um item do plano. `title` e os `body*` são o que a pessoa edita;
 *  `data` é o que veio da IA, intocado, pra quem aplica montar o
 *  payload com o que não é editável (fonte, categoria, peso…). */
export type PlanItem = {
  key: string;
  panel: PanelId;
  /** Linha curta: termo, nome, texto da regra. */
  title: string;
  /** Contexto que não se edita: "Redes · Positivo", grupo do tema. */
  subtitle?: string;
  /** Texto longo editável (premissas, regras da mensagem). */
  body?: string;
  bodyLabel?: string;
  /** Segundo texto longo (não identificar / exclusão). */
  body2?: string;
  body2Label?: string;
  exists: boolean;
  selected: boolean;
  data: PlanData;
};

export type PlanData =
  | { kind: "brand_terms"; current: string | null }
  | { kind: "citation"; category: string; current: string[] }
  | { kind: "focus"; category: FocusCategory; source: PostSource }
  | { kind: "rule"; ruleType: string; source: PostSource }
  | { kind: "term"; category: TermCategory; source: PostSource }
  | { kind: "group"; weight: number | null }
  | { kind: "theme"; group: string; terms: string | null }
  | { kind: "message"; source: PostSource }
  | { kind: "person" }
  | { kind: "tier"; weight: number; min: number | null; max: number | null };

export type PanelPlan = { panel: PanelId; label: string; items: PlanItem[] };

function key(...parts: (string | number)[]): string {
  return parts.map(part => foldText(String(part))).join("|");
}

function splitList(raw: string): string[] {
  return raw
    .split(/[;\n]/)
    .map(item => item.trim())
    .filter(Boolean);
}

export function planPanels(suggestion: ConfigSuggestion, catalog: Catalog): PanelPlan[] {
  const plans: Record<PanelId, PlanItem[]> = {
    brand: [],
    citation: [],
    focus: [],
    rules: [],
    terms: [],
    themes: [],
    messages: [],
    people: [],
    tiers: []
  };

  // Marca: um item só, a expressão de termos. "Existe" quando a
  // expressão sugerida é a que já está gravada.
  const currentTerms = catalog.brandTerms?.trim() || null;
  plans.brand.push({
    key: "brand-terms",
    panel: "brand",
    title: suggestion.brand_terms,
    subtitle: currentTerms ? `Cadastrado hoje: ${currentTerms}` : "Sem termos cadastrados",
    exists: currentTerms !== null && foldText(currentTerms) === foldText(suggestion.brand_terms),
    selected: false,
    data: { kind: "brand_terms", current: currentTerms }
  });

  const tagsByCategory = new Map(
    catalog.citationTags.map(group => [foldText(group.category), group.tags])
  );
  for (const [category, tags] of Object.entries(suggestion.citation_tags)) {
    const current = tagsByCategory.get(foldText(category)) ?? [];
    const known = new Set(current.map(foldText));
    const novos = tags.filter(tag => !known.has(foldText(tag)));
    plans.citation.push({
      key: key("citation", category),
      panel: "citation",
      title: category,
      subtitle: current.length ? `Cadastrados: ${current.join("; ")}` : "Nenhum cadastrado",
      body: novos.join("; "),
      bodyLabel: "Termos novos (separe por ;)",
      exists: novos.length === 0,
      selected: false,
      data: { kind: "citation", category, current }
    });
  }

  const focusKnown = new Set(
    catalog.focusTerms.filter(t => t.is_active).map(t => key(t.category, t.source, t.term))
  );
  for (const item of suggestion.focus) {
    const exists = focusKnown.has(key(item.category, item.source, item.term));
    plans.focus.push({
      key: key("focus", item.category, item.source, item.term),
      panel: "focus",
      title: item.term,
      subtitle: `${SOURCE_LABEL[item.source]} · ${item.category === "client" ? "Cliente" : "Concorrente"}`,
      exists,
      selected: false,
      data: { kind: "focus", category: item.category, source: item.source }
    });
  }

  const ruleKnown = new Set(
    catalog.promptRules
      .filter(r => r.is_active)
      .map(r => key(r.rule_type.replace(/_/g, ""), r.source, r.text))
  );
  suggestion.rules.forEach((rule, index) => {
    const exists = ruleKnown.has(key(rule.rule_type.replace(/_/g, ""), rule.source, rule.text));
    plans.rules.push({
      key: key("rule", rule.rule_type, rule.source, index),
      panel: "rules",
      title: rule.text,
      subtitle: `${SOURCE_LABEL[rule.source]} · ${RULE_TYPE_LABEL[rule.rule_type] ?? rule.rule_type}`,
      exists,
      selected: false,
      data: { kind: "rule", ruleType: rule.rule_type, source: rule.source }
    });
  });

  const termKnown = new Set(
    catalog.classificationTerms
      .filter(t => t.is_active && t.term_type === "proprietary_terms")
      .map(t => key(t.category, t.source, t.term))
  );
  for (const item of suggestion.proprietary_terms) {
    plans.terms.push({
      key: key("term", item.category, item.source, item.term),
      panel: "terms",
      title: item.term,
      subtitle: `${SOURCE_LABEL[item.source]} · ${item.category}`,
      exists: termKnown.has(key(item.category, item.source, item.term)),
      selected: false,
      data: { kind: "term", category: item.category, source: item.source }
    });
  }

  const groupKnown = new Set(catalog.themeGroups.filter(g => g.is_active).map(g => foldText(g.name)));
  for (const group of suggestion.theme_groups) {
    plans.themes.push({
      key: key("group", group.name),
      panel: "themes",
      title: group.name,
      subtitle: `Tema · peso ${group.weight}`,
      exists: groupKnown.has(foldText(group.name)),
      selected: false,
      data: { kind: "group", weight: group.weight }
    });
  }
  const themeKnown = new Set(catalog.themes.filter(t => t.is_active).map(t => foldText(t.name)));
  for (const theme of suggestion.themes) {
    plans.themes.push({
      key: key("theme", theme.name),
      panel: "themes",
      title: theme.name,
      subtitle: `Subtema · tema ${theme.group}`,
      body: theme.detection_assumptions,
      bodyLabel: "Identificar quando",
      body2: theme.non_detection_assumptions,
      body2Label: "Não identificar quando",
      exists: themeKnown.has(foldText(theme.name)),
      selected: false,
      data: { kind: "theme", group: theme.group, terms: theme.terms }
    });
  }

  const messageKnown = new Set(
    catalog.keyMessages.filter(m => m.is_active).map(m => key(m.data_source, m.message))
  );
  for (const message of suggestion.key_messages) {
    plans.messages.push({
      key: key("message", message.data_source, message.message),
      panel: "messages",
      title: message.message,
      subtitle: SOURCE_LABEL[message.data_source],
      body: message.rules,
      bodyLabel: "Quando aplicar",
      body2: message.exclusion_rules ?? "",
      body2Label: "Quando não aplicar",
      exists: messageKnown.has(key(message.data_source, message.message)),
      selected: false,
      data: { kind: "message", source: message.data_source }
    });
  }

  const personKnown = new Set(catalog.spokespersons.filter(p => p.is_active).map(p => foldText(p.name)));
  for (const person of suggestion.spokespersons) {
    plans.people.push({
      key: key("person", person.name),
      panel: "people",
      title: person.name,
      body: person.name_variations ?? "",
      bodyLabel: "Variações do nome (separe por ;)",
      exists: personKnown.has(foldText(person.name)),
      selected: false,
      data: { kind: "person" }
    });
  }

  const tierKnown = new Set(catalog.publisherTiers.filter(t => t.is_active).map(t => foldText(t.name)));
  for (const tier of suggestion.publisher_tiers) {
    plans.tiers.push({
      key: key("tier", tier.name),
      panel: "tiers",
      title: tier.name,
      subtitle: `Peso ${tier.weight} · ${tier.min_count ?? "—"} a ${tier.max_count ?? "∞"} seguidores`,
      exists: tierKnown.has(foldText(tier.name)),
      selected: false,
      data: { kind: "tier", weight: tier.weight, min: tier.min_count, max: tier.max_count }
    });
  }

  // Item novo nasce marcado; existente nasce desmarcado e assim fica.
  for (const items of Object.values(plans)) {
    for (const item of items) item.selected = !item.exists;
  }
  return PANEL_ORDER.filter(panel => plans[panel].length > 0).map(panel => ({
    panel,
    label: PANEL_LABELS[panel],
    items: plans[panel]
  }));
}

// --- aplicar -----------------------------------------------------------

/** O que o executor precisa da API — o subconjunto do `apiClient`, pra
 *  um fake de teste não ter que fingir 150 métodos. */
export type AssistantApi = {
  updateBrand(token: string, brandId: number, payload: { terms?: string | null }): Promise<unknown>;
  setCitationTags(token: string, category: string, tags: string[]): Promise<unknown>;
  createFocusTerm(
    token: string,
    payload: { brand_id: number; category: FocusCategory; source: PostSource; term: string }
  ): Promise<unknown>;
  createPromptRule(
    token: string,
    payload: { brand_id: number; task: string; rule_type: string; source: PostSource; text: string }
  ): Promise<unknown>;
  createClassificationTerm(
    token: string,
    payload: {
      brand_id: number;
      term_type: "proprietary_terms";
      category: TermCategory;
      source: PostSource;
      term: string;
    }
  ): Promise<unknown>;
  createThemeGroup(token: string, payload: { name: string; weight?: number | null }): Promise<ThemeGroup>;
  createTheme(
    token: string,
    payload: {
      brand_id: number;
      theme_group_id: number;
      name: string;
      terms?: string | null;
      detection_assumptions?: string | null;
      non_detection_assumptions?: string | null;
    }
  ): Promise<unknown>;
  createKeyMessage(
    token: string,
    payload: {
      brand_id: number;
      data_source: PostSource;
      message: string;
      rules: string;
      exclusion_rules?: string | null;
    }
  ): Promise<unknown>;
  createSpokesperson(
    token: string,
    payload: { brand_id: number; name: string; name_variations?: string | null }
  ): Promise<unknown>;
  createPublisherTier(
    token: string,
    payload: { name: string; weight: number; min_count?: number | null; max_count?: number | null }
  ): Promise<unknown>;
};

export type ApplyOutcome = {
  applied: string[];
  failed: { key: string; title: string; message: string }[];
};

/** O que deixa a mini base da calibração sem nada a medir, conferido
 *  antes de gerá-la (2026-10-27): a Danone (TESTE) foi do passo Sugestão
 *  direto pra Calibrar sem aplicar painel nenhum, a marca principal
 *  seguia sem termos, e as 60 linhas caíram na fila de correção com zero
 *  menção. `brandWithoutTerms` é o nome da marca quando o cadastro LIDO
 *  (relido depois de aplicar o painel da marca) não tem termos; sem
 *  catálogo não se afirma nada. `nothingApplied` só vale quando houve
 *  sugestão: sem ela não há painel pra aplicar. */
export function calibrationGaps(
  catalog: Catalog | null,
  hasSuggestion: boolean,
  outcomes: Record<string, ApplyOutcome>
): { brandWithoutTerms: string | null; nothingApplied: boolean } {
  return {
    brandWithoutTerms: catalog && !catalog.brandTerms?.trim() ? catalog.brandName : null,
    nothingApplied: hasSuggestion && Object.values(outcomes).every(outcome => outcome.applied.length === 0)
  };
}

// Era bug (consertado em 2026-10-09, teste de render): lia `error.message`
// direto, e um 422 por campo saía só como "Dados inválidos.", sem o
// "• campo: motivo" que o servidor mandou. `errorMessage` formata o
// `ApiError`; o `Error` local (recusa daqui mesmo) segue com a frase dele.
function messageOf(error: unknown): string {
  return errorMessage(error, error instanceof Error ? error.message : String(error));
}

/**
 * Grava os itens MARCADOS de um painel, um a um — uma falha não derruba
 * as outras, e cada uma volta com o item e o motivo pra tela listar.
 * No painel de temas os GRUPOS vão antes dos temas, e um tema cujo
 * grupo não existe nem foi criado agora falha com motivo, nunca cai
 * num grupo qualquer.
 */
export async function applyPanel(
  api: AssistantApi,
  token: string,
  panel: PanelId,
  items: PlanItem[],
  catalog: Catalog
): Promise<ApplyOutcome> {
  const outcome: ApplyOutcome = { applied: [], failed: [] };
  const chosen = items.filter(item => item.selected && !item.exists);
  const brandId = catalog.brandId;

  const run = async (item: PlanItem, call: () => Promise<unknown>) => {
    try {
      await call();
      outcome.applied.push(item.key);
    } catch (error) {
      outcome.failed.push({ key: item.key, title: item.title, message: messageOf(error) });
    }
  };

  if (panel === "themes") {
    const groupIds = new Map(
      catalog.themeGroups.filter(g => g.is_active).map(g => [foldText(g.name), g.id])
    );
    for (const item of chosen.filter(i => i.data.kind === "group")) {
      const data = item.data as Extract<PlanData, { kind: "group" }>;
      await run(item, async () => {
        const created = await api.createThemeGroup(token, {
          name: item.title.trim(),
          weight: data.weight
        });
        groupIds.set(foldText(item.title), created.id);
      });
    }
    for (const item of chosen.filter(i => i.data.kind === "theme")) {
      const data = item.data as Extract<PlanData, { kind: "theme" }>;
      const groupId = groupIds.get(foldText(data.group));
      if (groupId === undefined) {
        outcome.failed.push({
          key: item.key,
          title: item.title,
          message: `O tema "${data.group}" não existe e não foi criado — marque o tema também.`
        });
        continue;
      }
      await run(item, () =>
        api.createTheme(token, {
          brand_id: brandId,
          theme_group_id: groupId,
          name: item.title.trim(),
          terms: data.terms,
          detection_assumptions: item.body?.trim() || null,
          non_detection_assumptions: item.body2?.trim() || null
        })
      );
    }
    return outcome;
  }

  for (const item of chosen) {
    const data = item.data;
    switch (data.kind) {
      case "brand_terms":
        await run(item, () => api.updateBrand(token, brandId, { terms: item.title.trim() || null }));
        break;
      case "citation": {
        // O cadastrado vem primeiro e VENCE o empate: a grafia que a
        // pessoa já escolheu não é trocada pela da IA.
        const unique: string[] = [];
        const seen = new Set<string>();
        for (const tag of [...data.current, ...splitList(item.body ?? "")]) {
          if (seen.has(foldText(tag))) continue;
          seen.add(foldText(tag));
          unique.push(tag);
        }
        await run(item, () => api.setCitationTags(token, data.category, unique));
        break;
      }
      case "focus":
        await run(item, () =>
          api.createFocusTerm(token, {
            brand_id: brandId,
            category: data.category,
            source: data.source,
            term: item.title.trim()
          })
        );
        break;
      case "rule":
        await run(item, () =>
          api.createPromptRule(token, {
            brand_id: brandId,
            task: RULES_TASK,
            rule_type: data.ruleType,
            source: data.source,
            text: item.title.trim()
          })
        );
        break;
      case "term":
        await run(item, () =>
          api.createClassificationTerm(token, {
            brand_id: brandId,
            term_type: "proprietary_terms",
            category: data.category,
            source: data.source,
            term: item.title.trim()
          })
        );
        break;
      case "message":
        await run(item, () =>
          api.createKeyMessage(token, {
            brand_id: brandId,
            data_source: data.source,
            message: item.title.trim(),
            rules: item.body?.trim() ?? "",
            exclusion_rules: item.body2?.trim() || null
          })
        );
        break;
      case "person":
        await run(item, () =>
          api.createSpokesperson(token, {
            brand_id: brandId,
            name: item.title.trim(),
            name_variations: item.body?.trim() || null
          })
        );
        break;
      case "tier":
        await run(item, () =>
          api.createPublisherTier(token, {
            name: item.title.trim(),
            weight: data.weight,
            min_count: data.min,
            max_count: data.max
          })
        );
        break;
      case "group":
      case "theme":
        // Só o painel de temas chega aqui com esses tipos, e ele já
        // retornou acima.
        break;
    }
  }
  return outcome;
}
